import {
    Component
} from '@theme/component';
import {
    onAnimationEnd
} from '@theme/utilities';
import {
    ThemeEvents,
    CartUpdateEvent
} from '@theme/events';
import {
    shouldDeferCartSideEffects
} from '@theme/cart-add-perf';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
    requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

    /** @type {number} */
    get currentCartCount() {
        return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    }

    set currentCartCount(value) {
        this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    }

    connectedCallback() {
        super.connectedCallback();

        document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
        this.ensureCartBubbleIsCorrect();
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    }

    /**
     * Handles the cart update event.
     * @param {CartUpdateEvent} event
     */
    onCartUpdate = async (event) => {
        if (shouldDeferCartSideEffects(event)) return;

        const itemCount = event ?.detail ?.data ?.itemCount;
        if (typeof itemCount === 'number') {
            await this.renderCartBubble(itemCount, event.detail ?.data ?.action === 'add');
            return;
        }

        const cart = await fetch('/cart.js').then((res) => res.json());
        const absoluteItemCount = typeof cart ?.item_count === 'number' ? cart.item_count : 0;

        await this.renderCartBubble(absoluteItemCount, false);
    };

    /**
     * Renders the cart bubble.
     * @param {number} itemCount - Absolute number of items in the cart.
     * @param {boolean} shouldAnimate - Whether this should animate as an add-to-cart.
     */
    renderCartBubble = async (itemCount, shouldAnimate, animate = true) => {

        this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
        this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
        this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && shouldAnimate && animate);

        this.currentCartCount = itemCount;

        this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

        sessionStorage.setItem(
            'cart-count',
            JSON.stringify({
                value: String(this.currentCartCount),
                timestamp: Date.now(),
            })
        );

        if (!animate) return;
        await onAnimationEnd(this.refs.cartBubbleText);

        this.refs.cartBubble.classList.remove('cart-bubble--animating');
    };

    /**
     * Checks if the cart count is correct.
     */
    ensureCartBubbleIsCorrect = () => {
        const sessionStorageCount = sessionStorage.getItem('cart-count');
        const visibleCount = this.refs.cartBubbleCount.textContent;

        if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

        try {
            const {
                value,
                timestamp
            } = JSON.parse(sessionStorageCount);

            if (Date.now() - timestamp < 10000) {
                const count = parseInt(value, 10);

                if (count >= 0) {
                    this.renderCartBubble(count, false, false);
                }
            }
        } catch (_) {
            // no-op
        }
    };
}

if (!customElements.get('cart-icon')) {
    customElements.define('cart-icon', CartIcon);
}